import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, X, CheckCircle2 } from 'lucide-react';
import { trainingApi } from '../../api/training';
import { genericDocumentApi } from '../../api/generic-documents';
import { ApiError } from '../../api/client';
import { StatusBadge } from '../../components/StatusBadge';
import { ReturnAction } from '../../components/ReturnAction';
import { CommentsThread } from '../../components/CommentsThread';
import { DocumentsSection } from '../../components/DocumentsSection';
import { RecordHeader } from '../../components/RecordHeader';
import { WorkflowTrackingTab } from '../../components/WorkflowTrackingTab';
import { TRAINING_REQUEST_STAGES } from '../../config/workflow-stages';
import { WorkspaceTabs } from '../../components/WorkspaceTabs';
import { useToast } from '../../components/Toast';
import { usePermission } from '../../auth/AuthContext';
import { formatDate } from '../../utils/formatDate';

export function TrainingRequestDetailPage() {
  const { id } = useParams<{ id: string }>();
  const requestId = Number(id);
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const canManage = usePermission('training.manage');
  const [certificateNotes, setCertificateNotes] = useState('');

  const requestQuery = useQuery({ queryKey: ['training-request', requestId], queryFn: () => trainingApi.getById(requestId) });
  const docsQuery = useQuery({ queryKey: ['training-request-documents', requestId], queryFn: () => genericDocumentApi.listFor('employee_training', requestId) });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['training-request', requestId] });
  const decideMutation = useMutation({
    mutationFn: (decision: 'approved' | 'rejected') => trainingApi.hrDecide(requestId, { decision }),
    onSuccess: () => { showSuccess('Decision recorded.'); invalidate(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });
  const completeMutation = useMutation({
    mutationFn: () => trainingApi.complete(requestId, { certificateNotes }),
    onSuccess: () => { showSuccess('Marked completed.'); invalidate(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });

  if (!requestQuery.data) return <div className="page"><p className="muted">Loading…</p></div>;
  const r = requestQuery.data.data;

  return (
    <div className="page">
      <Link to="/hr/training-review">&larr; Back to Training - HR Review</Link>
      <RecordHeader
        title={`Training Request - ${r.employee_name ?? ''}`}
        entityType="employee_training"
        entityId={requestId}
        fields={[
          { label: 'Title', value: r.catalogue_title ?? r.training_title ?? '-' },
          { label: 'Provider', value: r.training_provider ?? '-' },
          { label: 'Proposed date', value: r.proposed_date ? formatDate(r.proposed_date) : '-' },
          { label: 'Supervisor decision', value: r.supervisor_decision ?? '-' },
        ]}
        actions={
          <span className="row-actions">
            {r.status === 'under_review' && (
              <>
                <button className="btn-primary" onClick={() => decideMutation.mutate('approved')} disabled={decideMutation.isPending}><Check size={16} className="inline-icon" /> Approve</button>
                <button className="button-danger" onClick={() => decideMutation.mutate('rejected')} disabled={decideMutation.isPending}><X size={16} className="inline-icon" /> Reject</button>
                <ReturnAction onReturn={(comment) => trainingApi.hrReturn(requestId, comment)} queryKeysToInvalidate={[['training-request', requestId]]} />
              </>
            )}
            {r.status === 'approved' && (
              <>
                <input placeholder="Certificate notes" value={certificateNotes} onChange={(e) => setCertificateNotes(e.target.value)} />
                <button className="btn-primary" onClick={() => completeMutation.mutate()} disabled={completeMutation.isPending}><CheckCircle2 size={16} className="inline-icon" /> Mark Completed</button>
              </>
            )}
          </span>
        }
        readOnly={!canManage}
      />
      <p className="muted">Status: <StatusBadge status={r.status} /></p>

      <WorkspaceTabs
        tabs={[
          {
            key: 'details',
            label: 'Details',
            content: (
              <>
                <section className="card">
                  <h3>Reason / Justification</h3>
                  <p>{r.reason}</p>
                  {r.return_comment && <p className="muted">Return comment: {r.return_comment}</p>}
                </section>
                <CommentsThread entityType="employee_training" entityId={requestId} />
              </>
            ),
          },
          {
            key: 'documents',
            label: 'Documents',
            content: (
              <DocumentsSection
                documents={docsQuery.data?.data ?? []}
                downloadUrl={(documentId) => genericDocumentApi.downloadUrl('employee_training', documentId)}
                upload={(input) => genericDocumentApi.uploadFor('employee_training', requestId, input)}
                onDelete={(documentId) => genericDocumentApi.deleteFor('employee_training', documentId)}
                queryKeyToInvalidate={['training-request-documents', requestId]}
                hrCategories={false}
              />
            ),
          },
          {
            key: 'tracking',
            label: 'Tracking',
            content: <WorkflowTrackingTab entityType="employee_training" entityId={requestId} actionStageMap={TRAINING_REQUEST_STAGES} />,
          },
        ]}
      />
    </div>
  );
}
